import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/looseend/EmptyState";
import { LoadingSkeleton } from "@/components/looseend/LoadingSkeleton";
import { HostedLoopInspector } from "@/components/looseend/HostedLoopInspector";
import { useInspector } from "@/components/looseend/inspector-context";
import { useHostedLoopDetail } from "@/lib/loops/product/hostedLoops";

export const Route = createFileRoute("/app/loops/$loopId")({
  validateSearch: (search: Record<string, unknown>): { action?: "verify" | "evidence" } => {
    const action = search["action"];
    return action === "verify" || action === "evidence" ? { action } : {};
  },
  head: () => ({
    meta: [
      { title: "Loop detail — L∞seEnd" },
      {
        name: "description",
        content: "The full record of one Loop: status, activity, evidence and next action.",
      },
      { property: "og:title", content: "Loop detail — L∞seEnd" },
      {
        property: "og:description",
        content: "Everything known about a single unresolved commitment.",
      },
    ],
  }),
  component: LoopDetailPage,
});

function LoopDetailPage() {
  const { loopId } = Route.useParams();
  const { action } = Route.useSearch();
  const { close } = useInspector();
  const detail = useHostedLoopDetail(loopId);
  const loop = detail.loop;

  if (detail.phase === "loading") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-8" role="status" aria-live="polite">
        <span className="sr-only">Loading this Loop</span>
        <LoadingSkeleton variant="detail" />
      </div>
    );
  }

  // A temporary outage is NEVER reported as a deleted Loop: the authority simply
  // could not be read, so the honest state is "try again", not "not found".
  if (detail.phase === "unavailable" || (!loop && detail.phase !== "not_found")) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <EmptyState
          title="This Loop can’t be shown right now"
          description={detail.message ?? "Your Loops are temporarily unavailable."}
          action={
            <Button variant="outline" onClick={detail.refresh}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (!loop) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <EmptyState
          title="Loop not found"
          description="It may have been deleted or the link is out of date."
          action={
            <Button asChild>
              <Link to="/app/queue">Back to the queue</Link>
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-8">
        <div className="mb-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/queue">
              <ArrowLeft className="size-4" aria-hidden="true" />
              Resolution Queue
            </Link>
          </Button>
        </div>
        <div className="overflow-hidden rounded-2xl border border-border">
          <HostedLoopInspector loopId={loop.id} onClose={close} initialAction={action ?? null} />
        </div>
      </div>
    </div>
  );
}
